import React, { useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContracts';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import { PACKAGE_NAMES } from '../../utils/constants';
import AdminSection from './AdminSection';
import ButtonLoader from '../common/ButtonLoader';

const AdminUserOps = ({ role, onChanged }) => {
  const { plans } = useContracts();
  const notify = useNotify();

  // Free Register
  const [regAddr, setRegAddr] = useState('');
  const [regRef, setRegRef] = useState('');
  const [regPkg, setRegPkg] = useState(1);
  const [regBusy, setRegBusy] = useState(false);

  // Free Upgrade
  const [upUid, setUpUid] = useState('');
  const [upPkg, setUpPkg] = useState(2);
  const [upBusy, setUpBusy] = useState(false);

  // Free Re-topup
  const [retUid, setRetUid] = useState('');
  const [retPkg, setRetPkg] = useState(1);
  const [retBusy, setRetBusy] = useState(false);

  const adminRegister = async () => {
    if (!plans?.write) return notify.error('Wallet', 'Connect wallet first');
    if (!ethers.utils.isAddress(regAddr)) return notify.error('Invalid', 'Bad address');
    setRegBusy(true);
    try {
      const tx = await plans.write.adminRegister(regAddr, regRef || 0, regPkg);
      notify.info('Submitting', 'Free registration sent…');
      await tx.wait();
      notify.success('Registered', `User registered with up to Tier ${regPkg}`);
      setRegAddr(''); setRegRef('');
      onChanged && onChanged();
    } catch (e) { notify.error('Failed', parseError(e)); }
    finally { setRegBusy(false); }
  };

  const adminUpgrade = async () => {
    if (!plans?.write) return notify.error('Wallet', 'Connect wallet first');
    if (!upUid || !/^\d+$/.test(upUid)) return notify.error('Invalid', 'Enter user ID');
    setUpBusy(true);
    try {
      const tx = await plans.write.adminUpgrade(upUid, upPkg);
      notify.info('Submitting', 'Free upgrade sent…');
      await tx.wait();
      notify.success('Upgraded', `User #${upUid} → Tier ${upPkg}`);
      setUpUid('');
      onChanged && onChanged();
    } catch (e) { notify.error('Failed', parseError(e)); }
    finally { setUpBusy(false); }
  };

  const adminRetopup = async () => {
    if (!plans?.write) return notify.error('Wallet', 'Connect wallet first');
    if (!retUid || !/^\d+$/.test(retUid)) return notify.error('Invalid', 'Enter user ID');
    setRetBusy(true);
    try {
      const tx = await plans.write.adminRetopup(retUid, retPkg);
      notify.info('Submitting', 'Free re-topup sent…');
      await tx.wait();
      notify.success('Re-topped up', `User #${retUid} re-topup applied to Tier ${retPkg}`);
      setRetUid('');
      onChanged && onChanged();
    } catch (e) { notify.error('Failed', parseError(e)); }
    finally { setRetBusy(false); }
  };

  return (
    <AdminSection
      title="Free User Operations"
      subtitle="Bypass USDT payment for registration / upgrade / re-topup. No income is distributed."
      role={role}
    >
      <div className="admin-grid-3">
        {/* Register */}
        <div className="admin-tile yellow">
          <div className="admin-tile-title">Free Register</div>
          <div className="admin-form">
            <div className="admin-form-row">
              <label>Address</label>
              <input value={regAddr} onChange={(e) => setRegAddr(e.target.value)} placeholder="0x…" />
            </div>
            <div className="admin-form-row">
              <label>Sponsor ID (optional)</label>
              <input value={regRef} onChange={(e) => setRegRef(e.target.value.replace(/[^0-9]/g, ''))} placeholder="123456" />
            </div>
            <div className="admin-form-row">
              <label>Activate up to Tier</label>
              <select value={regPkg} onChange={(e) => setRegPkg(Number(e.target.value))}>
                {PACKAGE_NAMES.map((n, i) => <option key={i} value={i + 1}>Tier {i + 1} – {n}</option>)}
              </select>
            </div>
            <button className="btn btn-primary btn-block" onClick={adminRegister} disabled={regBusy}>
              {regBusy ? <ButtonLoader label="Submitting" /> : 'Register'}
            </button>
          </div>
        </div>

        {/* Upgrade */}
        <div className="admin-tile green">
          <div className="admin-tile-title">Free Upgrade</div>
          <div className="admin-form">
            <div className="admin-form-row">
              <label>User ID</label>
              <input value={upUid} onChange={(e) => setUpUid(e.target.value.replace(/[^0-9]/g, ''))} placeholder="123456" />
            </div>
            <div className="admin-form-row">
              <label>Activate Tier</label>
              <select value={upPkg} onChange={(e) => setUpPkg(Number(e.target.value))}>
                {PACKAGE_NAMES.slice(1).map((n, i) => <option key={i} value={i + 2}>Tier {i + 2} – {n}</option>)}
              </select>
            </div>
            <button className="btn btn-success btn-block" onClick={adminUpgrade} disabled={upBusy}>
              {upBusy ? <ButtonLoader label="Submitting" /> : 'Upgrade'}
            </button>
          </div>
        </div>

        {/* Re-topup */}
        <div className="admin-tile yellow">
          <div className="admin-tile-title">Free Re-topup</div>
          <div className="admin-form">
            <div className="admin-form-row">
              <label>User ID</label>
              <input value={retUid} onChange={(e) => setRetUid(e.target.value.replace(/[^0-9]/g, ''))} placeholder="123456" />
            </div>
            <div className="admin-form-row">
              <label>Tier</label>
              <select value={retPkg} onChange={(e) => setRetPkg(Number(e.target.value))}>
                {PACKAGE_NAMES.map((n, i) => <option key={i} value={i + 1}>Tier {i + 1} – {n}</option>)}
              </select>
            </div>
            <button className="btn btn-primary btn-block" onClick={adminRetopup} disabled={retBusy}>
              {retBusy ? <ButtonLoader label="Submitting" /> : 'Re-topup'}
            </button>
          </div>
        </div>
      </div>
    </AdminSection>
  );
};

export default AdminUserOps;
